import { useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE, SECOND } from 'time-constants';

import { apiConfig } from '@constants';
import { vodLadingState } from '@store';

import { getDspAuthHeader } from '../../helpers/DspAuth';
import { isFireTV, isGoogleTV, isTizen, isTVOS, isVizio, isWeb, isX1 } from '../../helpers/Platform';

const getPlatform = () => {
  if (isGoogleTV) {
    return 'android_tv';
  }
  if (isFireTV) {
    return 'firetv';
  }
  if (isTizen) {
    return 'tizen';
  }
  if (isTVOS) {
    return 'apple_tv';
  }
  if (isWeb || isX1 || isVizio) {
    // TODO: Is this correct for vizio and x1?
    return 'website';
  }

  return 'website';
};

const field_values = ['promo', 'shortform', 'longform'];

const getFieldValue = data => data?.category?.custom_fields?.map(element => element?.field_value)[0] ?? null;

const validateChannels = (data, type) => {
  let newData = [];

  switch (type) {
    case 'shortform': {
      const newPlaylist = data?.[0]?.playlist?.filter(
        channel => channel?._id && channel?.title && channel?.duration && channel?.thumb,
      );

      newData.push({
        ...data[0],
        playlist: newPlaylist,
      });

      break;
    }
    case 'longform':
    case 'promo': {
      newData = data?.filter(channel => channel?._id && channel?.title && channel?.wallpaper);

      break;
    }
    default:
      break;
  }

  return newData;
};

const validateData = data => {
  if (data?.homepage?.length > 0) {
    const homepage = data?.homepage
      ?.filter(
        item =>
          // Check for channels items
          item?.channels?.length > 0 &&
          item?.category?.custom_fields?.find(
            element =>
              // Check if the field_title is valid
              element?.field_title === 'type' &&
              // Filter by promo, shortform or longform
              field_values?.includes(element?.field_value),
          ) &&
          // Make sure shortform playlist is not empty
          (getFieldValue(item) !== 'shortform' || item.channels[0].playlist?.length > 0),
      )
      .map(item => {
        const channels = validateChannels(item?.channels, getFieldValue(item));

        return { ...item, channels };
      });

    // Sort data by field_value, all shortform items at the top, then promo and finally longform items
    homepage.sort((a, b) => (getFieldValue(a) > getFieldValue(b) ? -1 : getFieldValue(b) > getFieldValue(a) ? 1 : 0));

    return {
      ...data,
      homepage,
    };
  }

  return data;
};

const getVODLanding = async (retry = false) => {
  try {
    const headers = await getDspAuthHeader(retry);
    const platform = getPlatform();
    const url = `${apiConfig.DSP_VOD_LANDING_URL}/${platform}/US?api_key=${apiConfig.DSP_API_KEY}`;
    const { data } = await axios.post(url, { headers });

    return validateData(data);
  } catch (error) {
    if (retry) {
      throw error;
    }
    return await getVODLanding(true);
  }
};

export const useGetVODLanding = () => {
  const [state, setState] = useRecoilState(vodLadingState);

  const { data, error, isFetched, isLoading } = useQuery(['getVODLanding'], () => getVODLanding(), {
    refetchOnWindowFocus: false,
    refetchInterval: 5 * MINUTE,
    retryDelay: 5 * SECOND,
  });

  useEffect(() => {
    setState({ data, error, isFetched, isLoading });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isFetched, isLoading]);

  return state;
};
